import {HttpParams} from "@angular/common/http";
import {Component, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {MatDialog} from "@angular/material/dialog";
import {ActivatedRoute} from "@angular/router";
import * as saveAs from "file-saver";
import {
	BehaviorSubject,
	filter,
	firstValueFrom,
	forkJoin,
	Observable,
	Subject,
	Subscription,
	takeUntil
} from "rxjs";
import {map, tap} from "rxjs/operators";
import {GeneralService} from "../../../../services/crud/general.service";
import {FilterControllerService} from "../../../../services/filter-controller.service";
import {
	TABLE_CANVAS_MARGIN,
	TABLE_MAX_COL_WIDTH,
	TABLE_MAX_HEADER_WIDTH,
	TABLE_MIN_COL_WIDTH,
	TableControllerService
} from "../../../../services/table-controller.service";
import {DialogComponent} from "libComm";
import {
	Data,
	DataSet,
	Layout,
	PageLayout,
	SectionLayout
} from "../../../../models/layout";
import {LayoutServiceService} from "../../../../services/layout.service";
import {SpinnerService} from "../../../../services/spinner.service";
import {
	getTextWidth,
	hasSubArray,
	refactorName,
	replacer,
	reviver,
	transformIdToValueFromList
} from "../../../../utils/generic";
import {ALERT_TYPE} from "../../alert/alert.enumerate";
import {AlertService} from "../../alert/alert.service";
import {PageEventType} from 'libComm';
import {DndComponent} from "../../dnd/dnd.component";
//ZZZ import { FILTER_TYPE, FilterInterface } from "../../filter/custom-filter/filter.interface";
import {FILTER_TYPE, FilterInterface} from "libComm";
import {TitleCasePipe} from "@angular/common";
import {TranslateService} from "@ngx-translate/core";
import {IMultiSelectOption} from "libComm";
import {cloneDeep, first} from "lodash";
import {SaveConfirmComponent} from "libComm";
import {ColumnSetting} from "../../../../models/announcements.model";
import {CustomActionsService} from "../../custom-actions/custom-actions.service";
import {ActionEvent} from "../../data-table/models/data-table.models";
import {DataTableComponent} from "../../data-table/data-table.component";

@Component({
	selector: "layout-general",
	templateUrl: "./general.component.html",
	styleUrls: ["./general.component.scss"],
	providers: [TableControllerService, FilterControllerService],
	host: {class: "h-100"}
})
export class GeneralComponent implements OnDestroy, OnInit
{
	@ViewChild(DataTableComponent) dataTable?: DataTableComponent;

	private searchSubscription?: Subscription;

	private _interactionCycle: Subject<void> = new Subject<void>();
	private _pageCycle: Subject<void> = new Subject<void>();

	private _routeUrl: BehaviorSubject<string> = new BehaviorSubject<string>("");


	private url$: Observable<string> = this.route.url.pipe(map((segments) => segments.join("/"))).pipe(
		map((url) =>
		{
			this.spinner.hideAll();
			this.apiUrl = url;
			this.route.parent?.url.pipe(map((segments) => segments.join("/"))).subscribe((finalParentUrl) =>
			{
				this.apiParent = finalParentUrl;
			});
			return url;
		}),
		filter((url) => url !== this._routeUrl.value),
		tap((url) =>
		{
			this._routeUrl.next(url);
			this.tableController.dataSet = [];

			this._interactionCycle.next();
			this.getQueryParam();
		}),
		takeUntil(this._pageCycle)
	);

	constructor(
		private spinner: SpinnerService,
		public tableController: TableControllerService,
		public filterController: FilterControllerService,
		private route: ActivatedRoute,
		private generalDataService: GeneralService,
		public dialog: MatDialog,
		public layoutService: LayoutServiceService,
		public alertService: AlertService,
		private translateService: TranslateService,
		private titlePageTranslate: TitleCasePipe,
		private customActionsService: CustomActionsService
	)
	{
	}

	ngOnDestroy(): void
	{
		if (!!this.searchSubscription)
		{
			this.searchSubscription.unsubscribe();
		}
		this._pageCycle.next();
	}

	ngOnInit(): void
	{
		this.initializeSubscriptions();
		this.initializeValidationRules();
		this.url$.subscribe();
	}

	/**
	 * Initializes event subscriptions with proper cleanup
	 */
	private initializeSubscriptions(): void
	{
		this.tableController.onPaginationChange.pipe(takeUntil(this._pageCycle)).subscribe((event) =>
		{
			this.savedPaginator = event;
			const isReset = event.isReset === true || event.pageIndex === 0;
			this.onSearch(
				event.pageIndex,
				event.loader,
				this.sortedColumns,
				this.savedFilter,
				this.datasetId,
				isReset
			);
		});

		this.tableController.onSortChange.pipe(takeUntil(this._pageCycle)).subscribe((event) =>
		{
			this.sortedColumns = event.sorting;
			sessionStorage.setItem(
				"sort_" + this.pageId,
				JSON.stringify(event.sorting, replacer)
			);
			this.onSearch(
				0,
				this.tableController.dataSet.length,
				event.sorting,
				this.savedFilter,
				this.datasetId,
				true
			);
		});
	}

	/**
	 * Initializes customizations for add click
	 */
	private initializeValidationRules(): void
	{
		this.tableController.customizeAfterAddClick = (newRowObj: any): any =>
		{
			delete newRowObj.lDocuments;
			delete newRowObj.backup;
			return newRowObj;
		};
	}

	layout: Layout | undefined;

	apiUrl: string = "";

	apiParent: string = "";

	savedFilter: any = [];

	titlePage: string = "";
	pageId: string = "";

	attachments: Document[] = [];

	savedParams: HttpParams = new HttpParams();

	savedPaginator: PageEventType = {
		pageIndex: 0,
		previousPageIndex: 0,
		loader: 50,
		size: 0
	};

	sortedColumns: Map<string, { order: number; type: "asc" | "desc" }> =
		new Map();

	disabledColumns: string[] = [
		"LAST_EXECUTION_DATE",
		"INSERT_USER",
		"INSERT_DATE",
		"UPDATE_USER",
		"UPDATE_DATE",
		"CREATED_AT",
		"UPDATED_AT",
		"ADDED_AT",
		"ADDED_BY"
	];

	datasetId: string = "";

	//ITOEM-05: added logic to display notification option button
	notificationOption: any[];
	//ITOEM-05: added logic to display notification option button
	manualOption: any[];

	// ============ HELPER GETTERS ============

	/**
	 * Returns the full API endpoint URL
	 */
	private get apiEndpoint(): string
	{
		return this.apiUrl ? `${this.apiParent}/${this.apiUrl}` : this.apiParent;
	}

	/**
	 * Returns the table section dataset
	 */
	private get tableDataset(): DataSet | undefined
	{
		return this.getSectionDataset("LYO_SECTABLE");
	}

	// ============ HELPER METHODS ============

	/**
	 * Gets a specific section's dataset by section ID
	 */
	private getSectionDataset(sectionId: string): DataSet | undefined
	{
		return this.layout?.sectionLayouts.find((s) => s.id === sectionId)?.dataset[0];
	}

	/**
	 * Checks if a value source string indicates remote data fetching
	 */
	private isRemoteValueSource(valueString: string): boolean
	{
		return ["table", "search_key", "return_value"].every((k) =>
			valueString.includes(k)
		);
	}

	/**
	 * Shows an alert message
	 */
	private showAlert(type: ALERT_TYPE, message: string, timeout = 5000): void
	{
		this.alertService.add({type, message, timeout, selfClose: null});
	}

	/**
	 * Opens a confirmation dialog and returns the result
	 */
	private openConfirmDialog(
		titleKey: string,
		contentKey: string,
		isDestructive = true
	): Observable<boolean>
	{
		return this.dialog.open(DialogComponent, {
			data: {
				title: this.translateService.instant(titleKey),
				content: this.translateService.instant(contentKey),
				cancelButtonLabel: this.translateService.instant("ACT_NO"),
				cancelButtonColor: "basic",
				confirmButtonLabel: this.translateService.instant("ACT_YES"),
				confirmButtonColor: isDestructive ? "warn" : "accent"
			}
		}).afterClosed();
	}

	/**
	 * Builds filter parameters from filter models
	 */
	private buildFilterParams(filterList: FilterInterface[]): string[]
	{
		const newFilter: string[] = [];

		filterList.filter((model) => !model.isHide).forEach((filter) =>
		{
			if (
				filter.filterSelectedOption.haveSecondParam &&
				filter.filterSelectedOption.id == "_-d_"
			)
			{
				if (this.checkStringOrNumberValidity(filter.firstValue))
					newFilter.push(filter.columnName + "_>d_" + filter.firstValue);
				if (this.checkStringOrNumberValidity(filter.secondValue))
					newFilter.push(filter.columnName + "_<d_" + filter.secondValue);
			}
			else
				if (
					filter.filterSelectedOption.haveSecondParam &&
					filter.filterSelectedOption.id == "_-_"
				)
				{
					if (this.checkStringOrNumberValidity(filter.firstValue))
						newFilter.push(filter.columnName + "_>_" + filter.firstValue);
					if (this.checkStringOrNumberValidity(filter.secondValue))
						newFilter.push(filter.columnName + "_<_" + filter.secondValue);
				}
				else
				{
					if (filter.filterSelectedOption.haveFirstParam)
					{
						if (
							(this.checkStringOrNumberValidity(filter.firstValue) &&
								filter.firstValue.length > 0) ||
							typeof filter.firstValue === "number"
						)
						{
							if (filter.filterType == FILTER_TYPE.MULTI_SELECTION)
							{
								newFilter.push(
									filter.columnName +
									filter.filterSelectedOption.id +
									filter.firstValue.join("§")
								);
							}else if(filter.filterType == FILTER_TYPE.TEXT && filter.filterSelectedOption.id == "_SAME_"){// OEMCC-5: logic to handle IN clause for FILTER_TYPE.TEXT
								let filterValue = filter.firstValue;
								let filterOption = filterValue.split(",");
								newFilter.push(filter.columnName + filter.filterSelectedOption.id + filterOption.join("§"));
							}else {
								newFilter.push(
									filter.columnName +
									filter.filterSelectedOption.id +
									filter.firstValue
								);
							}
						}
					}
					else
					{
						filter.firstValue = [];
						newFilter.push(
							filter.columnName + filter.filterSelectedOption.id + "#"
						);
					}
				}
		});

		return newFilter;
	}

	/**
	 * Performs a save operation for a single row
	 */
	private performRowSave(
		row: any,
		rowNumber: number,
		onSuccess?: () => void
	): void
	{
		this.spinner.showWithTitle(
			"data",
			this.translateService.instant("LOADING_SAVE")
		);
		delete row.editing;
		delete row.checked;

		this.generalDataService.postData(
			this.apiEndpoint,
			cloneDeep([row]),
			new HttpParams().set("new", row.newRow ?? false)
		).pipe(takeUntil(this._interactionCycle)).subscribe({
			next: (res) =>
			{
				this.spinner.hide();
				this.tableController.deletedRows = [];

				if (res.iResponseCode != 200)
				{
					this.spinner.hideAll();
					this.showAlert(
						ALERT_TYPE.DANGER,
						`${res.iResponseCode} - ${this.translateService.instant(
							res.sResponseMessage
						)}`
					);
					if (rowNumber >= 0)
					{
						this.tableController.dataSet[rowNumber].editing = true;
					}
					this.tableController.dataSet = cloneDeep(
						this.tableController.dataSet
					);
				}
				else
				{
					this.showAlert(
						ALERT_TYPE.SUCCESS,
						`${res.iResponseCode} - ${res.sResponseMessage}`,
						3000
					);
					this.onSearch(
						0,
						this.savedPaginator.loader,
						this.sortedColumns,
						this.savedFilter,
						this.datasetId,
						true
					);
					if (rowNumber >= 0)
					{
						this.tableController.dataSet[rowNumber] = res.oContent;
					}
					this.tableController.dataSet = cloneDeep(
						this.tableController.dataSet
					);
					onSuccess?.();

					// Reload visible inner tables after successful save
					this.dataTable?.reloadVisibleInnerTables();
				}

				this.tableController.rowNumber = -1;
			},
			error: (err) =>
			{
				this.spinner.hide();
				this.tableController.deletedRows = [];
				this.tableController.rowNumber = -1;
				this.showAlert(
					ALERT_TYPE.WARNING,
					`${err.error.data.iResponseCode} - ${err.error.data.sResponseMessage}`
				);
			}
		});
	}

	actionSetId: string = "";

	visibleItems!: number;

	hiddenColumns: string[] = []; //Hidden columns fetched for the user in the current page

	checkStringOrNumberValidity = (value: any | null) =>
	{
		return value !== undefined && value !== null;
	};

	trackBySectionLayout(index: number, sectionLayout: SectionLayout): string
	{
		return sectionLayout.id;
	}

	refreshData(
		srtdClmns: Map<
			string,
			{
				order: number;
				type: "asc" | "desc";
			}
		>                        = new Map(),
		newFilter: Array<string> = new Array<string>()
	)
	{
		this.onSearch(
			0,
			this.savedPaginator.loader,
			srtdClmns,
			newFilter,
			this.datasetId,
			true
		);
	}

	getQueryParam()
	{
		this.route.queryParams.pipe(
			takeUntil(this._interactionCycle),
			map((params) =>
			{
				this.pageId = params["id"] ?? [];
				this.titlePage = params["title"] ?? "";

				const sorted = sessionStorage.getItem("sort_" + this.pageId);

				if (sorted)
				{
					this.tableController.sortedColumns = JSON.parse(sorted, reviver);
				}
				else
				{
					this.tableController.sortedColumns = new Map<
						string,
						{ order: number; type: "asc" | "desc" }
					>();
				}

				const flt = localStorage.getItem("FLT_" + this.pageId);

				if (flt)
				{
					this.filterController.filterModels = JSON.parse(flt, reviver);
					this.onFilterClick(null, false);
				}

				this.getPageLayout(this.pageId);

				//ITOEM-05: added logic for notification button
				this.notificationOption = [];
				if (this.pageId != null && this.pageId == 'PAG_MONITOR')
				{
					this.generalDataService.getNotificationActions(this.apiParent + "/" + this.apiUrl, null).pipe(
						takeUntil(this._interactionCycle)
					).subscribe({
						next: (notificationOption) =>
						{
							this.notificationOption = notificationOption;
						},
						error: (err) =>
						{
							this.notificationOption = [];
						}
					});
				}

				//ITOEM-05: added logic for manual action
				this.manualOption = [];
				if (this.pageId != null && this.pageId == 'PAG_MONITOR')
				{
					this.generalDataService.getManualActions(this.apiParent + "/" + this.apiUrl, null).pipe(
						takeUntil(this._interactionCycle)
					).subscribe({
						next: (manualOption) =>
						{
							this.manualOption = manualOption;
						},
						error: (err) =>
						{
							this.manualOption = [];
						}
					});
				}

				return params;
			})
		).subscribe({
			next: (params) =>
			{
			},
			error: (err) =>
			{
				//(err)
				this.alertService.add({
					type: ALERT_TYPE.DANGER,
					message: "Some error occurred on layout retrive",
					timeout: 5000,
					selfClose: null
				});
			}
		});
	}

	// LZ: when a filter (in all pages) is applyed/searched, then it cause this event is triggered, and it stores filters status
	onFilterClick(event: any, isSearching: boolean = true)
	{
		localStorage.setItem(
			"FLT_" + this.pageId,
			JSON.stringify(this.filterController.filterModels, replacer)
		);

		const newFilter = this.buildFilterParams(
			this.filterController.filterModels
		);
		this.savedFilter = newFilter;
		this.filterController.refreshViewedFilter();

		if (isSearching)
		{
			this.tableController.dataSet = [];
			this.onSearch(
				0,
				this.savedPaginator.loader,
				this.sortedColumns,
				newFilter
			);
		}
	}

	onCancelClick()
	{
		this.tableController.deletedRows = [];
		this.tableController.selection.clear();
		this.tableController.dataSet = [];
		this.onSearch(
			0,
			this.savedPaginator.loader,
			this.sortedColumns,
			this.savedFilter
		);
	}

	onExportAllClick(event)
	{
		event.stopPropagation();

		if (this.layout)
		{
			this.savedParams = this.savedParams.set(
				"dataset",
				this.tableDataset?.id ?? ""
			);
		}

		this.generalDataService.exportDataExcel(this.apiEndpoint, "excel", this.savedParams, []).pipe(takeUntil(this._interactionCycle)).subscribe((blob) =>
		{
			saveAs(blob, `${this.titlePage}.xlsx`);
			this.spinner.hide();
		});
	}

	onSearch(
		page: number,
		size: number,
		sorting: Map<string, { order: number; type: "asc" | "desc" }>,
		filters: Array<string>,
		dts: string      = this.datasetId,
		refresh: boolean = false,
		extraParameters: Map<string, string | number | boolean> = new Map()

	)
	{
		let params = new HttpParams().set("page", page).set("size", size).set("dts", dts);

		new Map(
			[...sorting.entries()].sort((a, b) => a[1].order - b[1].order)
		).forEach((value, key, map) =>
		{
			if (!!!params.get("sort"))
				params = params.set(
					"sort",
					refactorName(key) + (value.type == "desc" ? ",desc" : "")
				);
			else
				params = params.append(
					"sort",
					refactorName(key) + (value.type == "desc" ? ",desc" : "")
				);
		});
		filters.forEach((filter) =>
		{
			if (!filter.includes("&"))
			{
				if (!!!params.get("search")) params = params.set("search", filter);
				else params = params.append("search", filter);
			}
			else
			{
				filter.split("&").forEach((value) =>
				{
					if (!!!params.get("search")) params = params.set("search", value);
					else params = params.append("search", value);
				});
			}
		});

		// ITOEM-18 added custom parameters if present
		extraParameters.forEach((value, key) =>
		{
			params = params.set(key, value.toString());
		});

		this.savedParams = params;

		this.spinner.showWithTitle(
			"data",
			this.translateService.instant("LOADING_RESULTSET")
		);

		this.tableController.selection.clear();

		this.searchSubscription = this.generalDataService.getData(this.apiEndpoint, undefined, params).pipe(takeUntil(this._interactionCycle)).subscribe({
			next: (res) =>
			{
				if (refresh)
				{
					const oldData = this.tableController.dataSet;
					const newData = res["oContent"]["rs"]["content"];

					// LM - Preserve open the rows that have a detail grid previously opened
					this.tableController.dataSet = newData.map((row, index) => ({
						...row,
						...(oldData[index] &&
						oldData[index].isInnerTableVisible !== undefined
							? {isInnerTableVisible: oldData[index].isInnerTableVisible}
							: {})
					}));

					if (!!this.layout) this.catchAllSelectedOptions(this.layout);

					// Reload all visible inner tables after main grid refresh
					this.dataTable?.reloadVisibleInnerTables();
				}
				else
				{
					// [MB 10/06/2025] ITOEM-55:
					// If page === 0, it means the batch size has changed, so we reset the data and start from the beginning.
					// If page !== 0, it means a "load more" action occurred, so we're fetching additional records from the DB
					// and appending them to the current result set.
					this.tableController.dataSet = page == 0 ?
						res["oContent"]["rs"]["content"] :
						hasSubArray(this.tableController.dataSet, res["oContent"]["rs"]["content"]);
				}

				this.tableController.numElementsLoaded =
					this.tableController.dataSet.length;
				this.tableController.numElementsSize =
					res["oContent"]["rs"].totalElements;

				this.filterController._filterValues.next(res["oContent"]["fv"]);
				this.tableController._filterValues.next(res["oContent"]["tv"]);
				this.calculateSizeComplete();
				this.spinner.hide();
			},
			error: (error) =>
			{
				this.spinner.hide();
			}
		});
	}

	/**
	 * This method handle all events from the grid base on it's event name
	 *
	 * @param event
	 * @returns
	 */
	actionCatcher(event: ActionEvent)
	{
		const {action, rowIndex, row} = event;

		switch (event.action)
		{
			case "ACT_IMPORT_ALL":
				this.importAll();
				break;
			case "ACT_EXPORT_ALL":
				this.exportAll();
				break;
			case "ACT_SAVE":
				this.saveAll();
				break;
			case "ACT_SAVE_ALL":
				this.saveAll();
				break;
			case "ACT_DELETE_ID":
				this.deleteRow();
				break;
			case "ACT_SAVE_ID":
				this.saveRow();
				break;
			case "ACT_DELETE":
				this.deleteMultipleRow();
				break;
			case "ACT_EXPORT":
				this.exportSelected();
				break;

			default: //Custom action of application domain
				this.customActionsService.handleCustomAction(action, {
					reloadData: this.reloadData,
					row,
					rowIndex,
					tableController: this.tableController,
					spinner: this.spinner,
					apiUrl: this.apiUrl,
					apiParent: this.apiParent,
					titlePage: this.titlePage,
					savedParams: this.savedParams,
					layout: this.layout,
					pageId: this.pageId,
					actionsets: this.layout.sectionLayouts.find((sectionLayout) => sectionLayout.id == "LYO_SECTABLE")?.actionset[0]
				});
		}
	}

	deleteOneRow()
	{
		this.openConfirmDialog("LABEL_DELETION_TITLE", "LABEL_CONFIRM").subscribe(
			(res) =>
			{
				if (res)
				{
					this.spinner.show();
					this.generalDataService.deleteData(this.apiEndpoint, this.tableController.deletedRows, "d").pipe(takeUntil(this._interactionCycle)).subscribe(() =>
					{
						this.tableController.deletedRows = [];
						this.spinner.hide();
						this.onCancelClick();
					});
				}
			}
		);
	}

	refreshDocumentData()
	{
		const params = new HttpParams().set(
			"id",
			this.tableController["LOADING_CARD_ID"]
		);

		this.generalDataService.getDocumentsById(`${this.apiEndpoint}/docs`, params).pipe(takeUntil(this._interactionCycle)).subscribe((res) =>
		{
			this.attachments = res["oContent"];
		});
	}

	private catchAllSelectedOptions(layout?: Layout)
	{
		layout?.sectionLayouts.forEach((sectionLayout: SectionLayout) =>
		{
			sectionLayout.dataset.forEach((dataset: DataSet) =>
			{
				dataset.datas?.forEach((data: Data) =>
				{
					if (
						this.isRemoteValueSource(data.valuesFrom) &&
						!data.isResultsetLinked
					)
					{
						this.generalDataService.getSelectionOption(this.replaceFilterValues(data.valuesFrom)).then((res) =>
						{
							data.optionValues = res;
						});
					}
					else
						if (data.fixListValues)
						{
							data.optionValues = this.parseFixListToOptions(data.fixListValues);
						}
				});

				dataset.filterset?.forEach((filter: FilterInterface) =>
				{
					if (
						filter.filterType == FILTER_TYPE.MULTI_SELECTION ||
						filter.filterType == FILTER_TYPE.SELECTION
					)
					{
						if (filter.filterValues)
						{
							if (
								this.isRemoteValueSource(filter.filterValueSearch) &&
								!filter.isResultsetLinked
							)
							{
								this.generalDataService.getSelectionOption(
									this.replaceFilterValues(filter.filterValueSearch)
								).then((res) =>
								{
									filter.filterValues = res;
								});
							}
							else
							{
								filter.filterValues = this.parseFixListToOptions(
									filter.filterValueSearch
								);
							}
						}
					}else
						if (filter.filterType == FILTER_TYPE.STATUS)
						{
							// ITOEM-5: added status filter
							// status filter value are structured as follow: ERROR-ERROR-error-F44336;WARNING-WARNING-warning-FFEB3B; INFO-INFO-info-4CAF50
							const list = filter.filterValueSearch.split(";");
							filter.filterValues = list.map(
								(options) =>
								{
									const optionList = options?.split("-") || [];

									return {
										id: optionList[0] || options,
										name: optionList[1] || options,
										matIcon: optionList[2] || "help",
										color: optionList[3] || "000"
									} as IMultiSelectOption;
								}
							);
						}
				});
			});
		});
	}

	/**
	 * Parses a semicolon-separated string into IMultiSelectOption array
	 */
	private parseFixListToOptions(fixListValues: string): IMultiSelectOption[]
	{
		return fixListValues.split(";").map((option) => ({id: option, name: option} as IMultiSelectOption));
	}

	private getPageLayout(pageId: string)
	{
		this.spinner.showWithTitle(
			"data",
			this.translateService.instant("LOADING_LAYOUT_OF") +
			this.titlePageTranslate.transform(this.titlePage)
		);
		//this.spinner.showWithTitle("data", "layout of " + this.titlePageTranslate.transform(this.titlePage));
		const newFilter: any = [];

		this.layoutService.getPageLayout(pageId).pipe(
			map((res: PageLayout | null) =>
			{
				this.layout = res?.layouts[0] ?? undefined;

				const tableSection = first(
					this.layout?.sectionLayouts.filter(
						(section) => section.id == "LYO_SECTABLE"
					) ?? ([] as SectionLayout[])
				);

				this.datasetId = tableSection?.dataset[0]?.id ?? "";
				this.actionSetId = tableSection?.actionset[0]?.id ?? "";
				this.tableController.setDatasets(tableSection?.dataset[0] ?? null);

				const filterSettings = sessionStorage.getItem("filter");

				if (!!filterSettings)
				{
					const filters = JSON.parse(filterSettings);
					if (!!filters[this.pageId] && !!filters[this.pageId].instance)
					{
						let oldFilters: FilterInterface[] = [];

						const flt = localStorage.getItem("FLT_" + this.pageId);
						if (flt)
						{
							oldFilters = JSON.parse(flt, replacer);
						}
						else
						{
							oldFilters = cloneDeep(filters[this.pageId].instance);
						}

						const newFilters = cloneDeep(
							this.layout?.sectionLayouts?.filter((sectionLayout) => sectionLayout.id == "LYO_SECFILT").flatMap((section) => section.dataset).flatMap((datasets) => datasets.filterset).filter((flts) => flts != null)
						) as FilterInterface[];

						/*const newFilters = cloneDeep(this.layout?.sectionLayouts.filter((sectionLayout) => sectionLayout.id == "LYO_SECFILT").pop()
									?.dataset[0]?.filterset ?? [])*/

						this.filterController.filterModels = newFilters.map((fltInt) =>
						{
							for (let f of oldFilters)
							{
								const flt = cloneDeep(f);

								if (fltInt.filterId == flt.filterId)
								{
									fltInt.firstValue = flt.firstValue;
									fltInt.filterType = flt.filterType;
									fltInt.lFilterOptions = flt.lFilterOptions;
									fltInt.columnLabel = flt.columnLabel;
									fltInt.columnName = flt.columnName;
									fltInt.filterValues = flt.filterValues;
									fltInt.filterValueSearch = flt.filterValueSearch;
									fltInt.selectedOption = flt.selectedOption;
									fltInt.filterSelectedOption = flt.filterSelectedOption;
								}
							}

							return fltInt;
						});
					}
					else
					{
						this.filterController.filterModels =
							this.layout?.sectionLayouts.filter((sectionLayout) => sectionLayout.id == "LYO_SECFILT").pop()?.dataset[0]?.filterset ?? [];
					}
					this.filterController.refreshViewedFilter();
				}
				else
				{
					this.filterController.filterModels =
						this.layout?.sectionLayouts.filter((sectionLayout) => sectionLayout.id == "LYO_SECFILT").pop()?.dataset[0]?.filterset ?? [];
					this.filterController.refreshViewedFilter();
				}

				const filterString = localStorage.getItem("FLT_" + this.pageId);

				if (!!filterString)
				{
					const _filterList: FilterInterface[] = JSON.parse(
						filterString,
						replacer
					);

					if (_filterList.length > 0)
					{
						this.filterController.filterModels =
							this.filterController.filterModels.map((flt) =>
							{
								const matchingFilter = _filterList.find(
									(f) => f.columnName == flt.columnName
								);
								return matchingFilter ?? flt;
							});
					}

					newFilter.push(
						...this.buildFilterParams(this.filterController.filterModels)
					);
					this.filterController.refreshViewedFilter();
				}

				//Dynamic hide columns from the preferences of the user
				const columnSetting: string | null = sessionStorage.getItem(
					`${this.pageId}_COLUMN_SETTINGS`
				);
				if (columnSetting)
				{
					//We found a valid key for the current page_id
					const columnSettingParsed: ColumnSetting =
							  JSON.parse(columnSetting);
					this.hiddenColumns = columnSettingParsed.hiddenColumns;

					//Now search in the layouts ...
					if (this.layout && this.layout.sectionLayouts)
					{
						for (const layout of this.layout.sectionLayouts)
						{
							for (const dataset of layout.dataset)
							{
								if (dataset.datas)
								{
									//Add flag hidden: true for each column tha is hide
									dataset.datas = dataset.datas.map((data) =>
									{
										if (columnSettingParsed.hiddenColumns.includes(data.id))
										{
											return {
												...data,
												hidden: true
											};
										}
										else
										{
											return data;
										}
									});
								}
							}
						}
					}
				}

				const layoutsString = localStorage.getItem("layout");

				const sorted = sessionStorage.getItem("sort_" + this.pageId);

				if (sorted)
				{
					this.tableController.sortedColumns = JSON.parse(sorted, reviver);
				}
				else
				{
					const dtArray: Data[] = Array.from(
						new Set(
							(res?.layouts ?? ([] as Layout[])).flatMap((l) =>
							{
								return l.sectionLayouts;
							}).flatMap((sl) => sl.dataset).flatMap((dts) => dts.datas ?? ([] as Data[]))
						)
					);

					const ds =
							  dtArray.filter((d) => d.sortOrder).sort((a, b) => a.sortOrder! - b.sortOrder!) ?? [];

					this.sortedColumns = new Map(
						ds.map((d) => [
							d.columnName,
							{order: d.sortOrder!, type: d.sortCriteria ?? "desc"}
						])
					);
					this.tableController.sortedColumns = this.sortedColumns;
				}

				if (!!layoutsString)
				{
					let layouts = JSON.parse(layoutsString);

					layouts[pageId] = {
						instance: res?.layouts[0],
						instant: new Date()
					};

					localStorage.setItem("layout", JSON.stringify(layouts));
				}
				else
				{
					let layouts = {};

					layouts[pageId] = {
						instance: res?.layouts[0],
						instant: new Date()
					};

					localStorage.setItem("layout", JSON.stringify(layouts));
				}
			})
		).pipe(takeUntil(this._interactionCycle)).subscribe((res) =>
		{
			this.spinner.hide();
			this.refreshData(this.tableController.sortedColumns, newFilter);
		});
	}

	dateRowAnalyze(dateRow: string[], committedRow: any[]): boolean
	{
		// Check if there are dates to validate
		if (dateRow.length === 0)
		{
			return true;
		}

		// Check if all dates in committedRow are valid
		return dateRow.every((key) =>
		{
			const dateValue = committedRow[key];

			// Check if the date exists and is valid
			return dateValue !== undefined && this.isValidDate(dateValue);
		});
	}

	isValidDate(dateString: string): boolean
	{
		// Consider an empty string as valid
		if (dateString === "")
		{
			return true;
		}
		else
			if (dateString === null) return true;

		// RegEx to validate the format
		// DD/MM/YYYY or DD/MM/YYYY hh:mm:ss
		const regex =
				  /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}(?:\s([01][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9])?$/;

		return regex.test(dateString);
	}

	// LZ: when a filter reset button is pressed then this event is triggered
	resetFilter(isResetting: boolean)
	{
		if (isResetting)
		{
			this.getPageLayout(this.pageId);
		}
	}

	replaceFilterValues(valuesFrom: string)
	{
		let researchString = cloneDeep(valuesFrom);

		const replaceArray =
				  valuesFrom.match(/_@[^\ ]*@_/gm)?.map((substr) =>
				  {
					  return {
						  toReplace: substr,
						  raplaceWith: ""
					  };
				  }) ?? [];

		for (let i = 0; i < replaceArray.length; i++)
		{
			researchString = researchString.replace(
				replaceArray[i].toReplace,
				"'" + replaceArray[i]?.raplaceWith?.replace(/\//gm, "-") + "'"
			);
		}

		return researchString;
	}

	/**
	 * Calculates the size of each column based on the content of the cells and the column label and options, this is necessary to set the width of the columns in the table, and avoid too wide or too narrow columns
	 */
	calculateWidthOfColumnsByContent(): Record<string, number>
	{
		if (!this.layout) return {};

		const HEADER_FONT = "700 14px ElectroluxSans";
		const ROW_FONT = "400 14px ElectroluxSans";

		const columns: Data[] = this.layout.sectionLayouts[0].dataset[0].datas ?? [];
		const columnMaxWidth: Record<string, number> = {};

		// Build column metadata keyed by columnName and initialize widths with header widths
		const columnDetails = new Map<string, ColumnDetail>();
		for (const col of columns)
		{
			const headerWidth = Math.min(
				getTextWidth(this.translateService.instant(col.columnLabel), HEADER_FONT),
				TABLE_MAX_HEADER_WIDTH
			);

			columnDetails.set(col.columnName, {width: headerWidth, optionValues: col.optionValues});
			columnMaxWidth[col.columnName] = Math.max(columnMaxWidth[col.columnName] ?? 0, headerWidth);
		}

		// Measure cell content widths (uncapped) and keep the max per column
		// DTY_LIST cells render their label from _filterValues (the "tv" map), so resolve the
		// displayed text from the same source the cell uses (col.optionValues is loaded async and
		// is often still empty here, which made list columns collapse to the header width).
		const filterValues = this.tableController._filterValues.getValue() ?? {};
		for (const row of this.tableController.dataSet)
		{
			for (const key of Object.keys(row))
			{
				const detail = columnDetails.get(key);
				const options = filterValues[key];
				let displayText: string;

				if (options?.length)
				{
					displayText = options.find((o: any) => o.id == row[key])?.name ?? ("" + (row[key] ?? ""));
				}
				else if ((detail?.optionValues?.length ?? 0) > 0)
				{
					displayText = transformIdToValueFromList(detail, row[key], row[key]) ?? "";
				}
				else
				{
					displayText = "" + (row[key] ?? "");
				}

				const cellWidth = getTextWidth(displayText, ROW_FONT);
				columnMaxWidth[key] = Math.max(columnMaxWidth[key] ?? 0, cellWidth);
			}
		}

		// pad (canvas-measurement safety margin) and clamp into the final weight
		const columnWeights: Record<string, number> = {};
		for (const key of Object.keys(columnMaxWidth))
		{
			const paddedWidth = columnMaxWidth[key] + TABLE_CANVAS_MARGIN;
			columnWeights[key] = Math.min(Math.max(paddedWidth, TABLE_MIN_COL_WIDTH), TABLE_MAX_COL_WIDTH);
		}

		return columnWeights;
	}

	resetSort()
	{
		const dtArray: Data[] = Array.from(
			new Set(
				this.layout?.sectionLayouts?.flatMap((sl) => sl.dataset).flatMap((dts) => dts.datas ?? ([] as Data[]))
			)
		);

		const ds =
				  dtArray.filter((d) => d.sortOrder).sort((a, b) => a.sortOrder! - b.sortOrder!) ?? [];

		this.sortedColumns = new Map(
			ds.map((d) => [
				d.columnName,
				{order: d.sortOrder!, type: d.sortCriteria ?? "desc"}
			])
		);

		this.tableController.sortedColumns = this.sortedColumns;

		sessionStorage.setItem(
			"sort_" + this.pageId,
			JSON.stringify(this.sortedColumns, replacer)
		);
		this.onSearch(
			0,
			this.tableController.dataSet.length,
			this.sortedColumns,
			this.savedFilter,
			this.datasetId,
			true
		);
	}

	/**
	 * Calculates the size of each column based on the content of the cells and the column label, then sets the column weight in the table controller and calculates the column width
	 * @private
	 */
	private calculateSizeComplete()
	{
		this.tableController.columnWeight = this.calculateWidthOfColumnsByContent();
		this.tableController.calculateColumnWidth();
	}

	/**
	 * Reloads the grid applying current settings.
	 *
	 * Starts a new search from the first page with:
	 * - this.savedPaginator.loader: row limit to load.
	 * - this.sortedColumns: current sort settings.
	 * - this.savedFilter: persisted filters.
	 * - this.dts: dataset identifier.
	 * - refreshForcing = true: forces refresh avoiding stale data.
	 */
	reloadData = (extraParameters: Map<string, string | number | boolean> = new Map()): void =>
	{
		this.onSearch(
			0,
			this.savedPaginator.loader,
			this.sortedColumns,
			this.savedFilter,
			this.datasetId,
			true,
			extraParameters
		);
	};

	private saveAll()
	{
		const editedRows = this.tableController.dataSet.filter((row) => row.edited);
		const dateColumns = this.getDateColumnNames();

		if (editedRows.length === 0)
		{
			this.showAlert(ALERT_TYPE.WARNING, "No data change");
			return;
		}

		editedRows.forEach((row, index) =>
		{
			if (!this.dateRowAnalyze(dateColumns, row))
			{
				this.showAlert(ALERT_TYPE.WARNING, "Invalid Date");
			}
			else
			{
				const rowIndex = this.tableController.dataSet.indexOf(row);
				this.performRowSave(cloneDeep(row), rowIndex);
			}
		});
	}

	/**
	 * Gets the column names that are of type DTY_DATE
	 */
	private getDateColumnNames(): string[]
	{
		return (
			this.layout?.sectionLayouts?.[2]?.dataset?.[0]?.datas?.filter((data) => data.type === "DTY_DATE")?.map((data) => data.columnName) || []
		);
	}

	private saveRow(): void
	{
		const rowNumber = cloneDeep(this.tableController.rowNumber);
		this.tableController.rowNumber = -1;

		const committedRow = cloneDeep(this.tableController.dataSet[rowNumber]);
		const dateColumns = this.getDateColumnNames();
		const validDate = this.dateRowAnalyze(dateColumns, committedRow);

		if (!committedRow.edited)
		{
			this.showAlert(ALERT_TYPE.WARNING, "No data change");
			return;
		}

		if (!validDate)
		{
			this.showAlert(ALERT_TYPE.WARNING, "Invalid Date");
			return;
		}

		const editedRows = this.tableController.dataSet.filter((row) => row.edited);

		if (editedRows.length == 1)
		{
			this.performRowSave(committedRow, rowNumber);
		}
		else
		{
			this.openSaveConfirmDialog().subscribe((res) =>
			{
				if (res == "one")
				{
					this.performRowSave(committedRow, rowNumber);
				}
				else
					if (res == "all")
					{
						this.saveAll();
					}
			});
		}
	}

	/**
	 * Opens a dialog to confirm saving one or all rows
	 */
	private openSaveConfirmDialog(): Observable<string>
	{
		return this.dialog.open(SaveConfirmComponent, {
			data: {
				title: this.translateService.instant("LABEL_SAVE_ALL"),
				content: this.translateService.instant("LABEL_MANY_PENDING_CHANGES"),
				cancelButtonLabel: this.translateService.instant("ACT_NO"),
				cancelButtonColor: "basic",
				allButtonLabel: this.translateService.instant("Save all"),
				allButtonColor: "accent",
				confirmButtonLabel: this.translateService.instant("ACT_YES"),
				confirmButtonColor: "accent"
			}
		}).afterClosed();
	}

	private deleteRow(): void
	{
		this.openConfirmDialog("LABEL_DELETE_TITLE", "LABEL_CONFIRM").subscribe(
			(res) =>
			{
				if (res)
				{
					this.spinner.showWithTitle(
						"data",
						this.translateService.instant("LOADING_DELETE")
					);

					this.generalDataService.deleteData(
						this.apiEndpoint,
						[this.tableController.dataSet[this.tableController.rowNumber]],
						"d"
					).pipe(takeUntil(this._interactionCycle)).subscribe({
						next: (res) =>
						{
							this.tableController.dataSet.splice(
								this.tableController.rowNumber,
								1
							);
							this.tableController.dataSet = cloneDeep(
								this.tableController.dataSet
							);
							this.resetDeleteState();
							this.spinner.hide();
							this.showAlert(
								ALERT_TYPE.SUCCESS,
								`${res.iResponseCode} - ${res.sResponseMessage}`,
								3000
							);
							this.onCancelClick();
						},
						error: (err) =>
						{
							this.resetDeleteState();
							this.tableController.dataSet = cloneDeep(
								this.tableController.dataSet
							);
							this.spinner.hide();
							this.showAlert(
								ALERT_TYPE.WARNING,
								`${err.error.data.iResponseCode} - ${err.error.data.sResponseMessage}`
							);
						}
					});
				}
				else
				{
					this.tableController.deletedRows = [];
				}
			}
		);
	}

	/**
	 * Resets the delete state in the table controller
	 */
	private resetDeleteState(): void
	{
		this.tableController.deletedRows = [];
		this.tableController.rowNumber = -1;
	}

	private exportAll(): void
	{
		if (this.tableController.numElementsSize > 5000)
		{
			this.openConfirmDialog(
				"LABEL_LARGE_AMOUNT_DATA",
				"LABEL_LARGE_AMOUNT_DATA_CONFIRM"
			).subscribe((res) =>
			{
				if (res)
				{
					this.performExport([]);
				}
			});
		}
		else
		{
			this.performExport([]);
		}
	}

	/**
	 * Performs the actual export operation
	 */
	private performExport(selectedRows: any[]): void
	{
		this.spinner.showWithTitle(
			"data",
			`${this.translateService.instant("LOADING_EXPORT")} ${this.titlePage}`
		);

		const params = this.savedParams.set("dataset", this.tableDataset?.id ?? "");

		this.generalDataService.exportDataExcel(this.apiEndpoint, "excel", params, selectedRows).pipe(takeUntil(this._interactionCycle)).subscribe((blob) =>
		{
			saveAs(blob, `${this.titlePage}.xlsx`);
			this.spinner.hide();
		});
	}

	private importAll(): void
	{
		const dialogEvent = this.dialog.open(DndComponent, {
			data: {
				title: "UPLOADING",
				code: "Upload file: ",
				message: "Are you sure you want to upload this file?",
				btn: "Upload",
				type: "any",
				cancelBtn: "Close"
			}
		});

		dialogEvent.afterClosed().subscribe(async (dndResponse) =>
		{
			if (dndResponse?.send)
			{
				for (const file of dndResponse.files as File[])
				{
					await this.processImportFile(file);
				}
			}
		});
	}

	/**
	 * Processes a single import file
	 */
	private async processImportFile(file: File): Promise<void>
	{
		this.spinner.showWithTitle(
			"data",
			`${this.translateService.instant("LOADING_IMPORT")} ${this.titlePage}`
		);

		try
		{
			const res = await firstValueFrom(
				this.generalDataService.importExcel(`${this.apiEndpoint}/import`, file)
			);

			if (res.type === "application/json")
			{
				const stringResponse = await res.text();
				const response = JSON.parse(stringResponse);

				if (response.data.iResponseCode !== 200)
				{
					this.showAlert(ALERT_TYPE.WARNING, response.data.oContent, 10000);
				}
				else
				{
					this.showAlert(ALERT_TYPE.SUCCESS, "File Uploaded");
				}
			}
			else
			{
				if (res.size != 0)
				{
					saveAs(res, `${this.titlePage}_RESULT.xlsx`);
				}
				this.showAlert(ALERT_TYPE.DANGER, "File Uploaded with Errors");
			}

			this.onSearch(
				0,
				this.savedPaginator.loader,
				this.sortedColumns,
				this.savedFilter,
				this.datasetId,
				true
			);
		}
		catch (err)
		{
			this.showAlert(
				ALERT_TYPE.WARNING,
				"Something went wrong, please try again or contact webmaster"
			);
		}
		finally
		{
			this.spinner.hide();
		}
	}

	private deleteMultipleRow(): void
	{
		this.openConfirmDialog(
			"LABEL_DELETE_TITLE",
			"LABEL_DELETE_CONFIRM"
		).subscribe((res) =>
		{
			if (res && this.tableController.selection.selected.length > 0)
			{
				const selectedRows = this.tableController.selection.selected;

				this.spinner.showWithTitle(
					"data",
					this.translateService.instant("LOADING_DELETE")
				);

				forkJoin(
					selectedRows.map((row) =>
						this.generalDataService.deleteData(this.apiEndpoint, [row], "d")
					)
				).pipe(
					tap(() => this.spinner.hide()),
					takeUntil(this._interactionCycle)
				).subscribe({
					next: (results) =>
					{
						results.forEach((result) =>
						{
							const type =
									  result.iResponseCode < 300
										  ? ALERT_TYPE.SUCCESS
										  : ALERT_TYPE.DANGER;
							this.showAlert(
								type,
								`${result.iResponseCode} - ${result.sResponseMessage}`,
								3000
							);
						});

						if (!results.some((r) => r.iResponseCode >= 300))
						{
							this.tableController.selection.clear();
							this.resetDeleteState();
						}

						this.onCancelClick();
					},
					error: (err) =>
					{
						this.tableController.selection.clear();
						this.resetDeleteState();
						this.spinner.hide();
						this.showAlert(
							ALERT_TYPE.WARNING,
							`${err.error.data.iResponseCode} - ${err.error.data.sResponseMessage}`
						);
					}
				});
			}
		});
	}

	private exportSelected(): void
	{
		this.performExport(this.tableController.selection.selected);
	}
}

interface ColumnDetail
{
	width: number;
	optionValues: any[];
}
